import { Box, Button, TextField } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useNotify } from "react-admin";

import CustomCard from "../../components/CustomCard";
import CustomSelect from "../../components/CustomSelect";
import Loader from "../../components/Loader";
import useCurrentUser from "../../hooks/useCurrentUser";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import useGetEnumsAsChoices from "../../hooks/useGetEnumsAsChoices";
import useIsAcademy from "../../hooks/useIsAcademy";
import useIsCoach from "../../hooks/useIsCoach";
import useIsEntertainment from "../../hooks/useIsEntertainment";
import useIsGym from "../../hooks/useIsGym";
import useIsSportsClub from "../../hooks/useIsSportsClub";
import useIsStore from "../../hooks/useIsStore";
import { Enums } from "../../types/resource.types";
import {
  sportClubSettingsCollRef,
  brandSettingsCollRef,
  gymSettingsCollRef,
  academySettingsCollRef,
  coachSettingsCollRef,
  entertainmentSettingsCollRef,
} from "../../utils/referencies";
import { applyUpdateTimestamp } from "../../utils/timestamp";

const AccountSettings = () => {
  const paymentMethodsEnums = useGetEnumsAsChoices<Enums>(Enums.paymentMethods);
  const { user } = useCurrentUser();
  const { isSportsClub } = useIsSportsClub();
  const { isGym } = useIsGym();
  const { isStore } = useIsStore();
  const { isAcademy } = useIsAcademy();
  const { isCoach } = useIsCoach();
  const { isEntertainment } = useIsEntertainment();
  const notify = useNotify();

  const [settings, setSettings] = useState<any>({});
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { disabledStyles } = useDisabledOnLoading(loadingSubmit);

  const reservationTypes = [
    { id: "indoor", name: "In House" },
    { id: "outdoor", name: "Out Door" },
  ];

  const userRoleLogining = (): any => {
    if (isSportsClub) {
      return sportClubSettingsCollRef(user.id!);
    } else if (isStore) {
      return brandSettingsCollRef(user.id!);
    } else if (isGym) {
      return gymSettingsCollRef(user.id!);
    } else if (isAcademy) {
      return academySettingsCollRef(user.id!);
    } else if (isCoach) {
      return coachSettingsCollRef(user.id!);
    } else if (isEntertainment) {
      return entertainmentSettingsCollRef(user.id!);
    }

    return null;
  };

  const handleSelectSettingsPaymentsMethods = (selectedPayments: any) => {
    setSettings((prevSettings: any) => {
      return {
        ...prevSettings,
        paymentMethods: selectedPayments,
      };
    });
  };

  const handleSelectSettingsReservationTypes = (selectedTypes: any) => {
    setSettings((prevSettings: any) => {
      return {
        ...prevSettings,
        reservationTypes: selectedTypes,
      };
    });
  };

  const handleSubmit = async () => {
    setLoadingSubmit(true);
    const { ref, ...finalSettings } = settings;
    try {
      await ref.update(
        applyUpdateTimestamp({
          ...finalSettings,
          reservationReminder: settings.reservationReminder ? settings.reservationReminder * 60 * 60 * 1000 : null,
        })
      );
      notify("Settings has been updated successfully", "success");

      setLoadingSubmit(false);
    } catch (ex: any) {
      console.log(ex.message);
      setLoadingSubmit(false);
      notify("Error on settings", "error");
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const doc = await userRoleLogining()!.get();
      const reservationReminder = doc.data().reservationReminder
        ? doc.data().reservationReminder / 1000 / 60 / 60
        : null;
      setSettings({ ...doc.data(), ref: doc.ref, reservationReminder });

      setLoading(false);
    } catch (ex: any) {
      console.log(ex.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (settings) {
      setDisabled(!settings.paymentMethods?.length);
      if (isCoach) {
        setDisabled(!settings.reservationTypes?.length || !settings.paymentMethods?.length);
      }
      if (settings.reservationReminder) {
        setDisabled(settings.reservationReminder < 0);
      }
    }
  }, [settings]);

  return (
    <Loader loading={loading}>
      <Box style={disabledStyles}>
        <CustomCard title="Payment Methods" width="50%">
          <CustomSelect
            multiple
            selectedItem={settings.paymentMethods ?? []}
            handleSelectItem={handleSelectSettingsPaymentsMethods}
            labelTitle="Payment Methods"
            listItems={paymentMethodsEnums}
          />
        </CustomCard>
        {isCoach && (
          <CustomCard title="Reservation Types" width="50%">
            <CustomSelect
              multiple
              selectedItem={settings.reservationTypes ?? []}
              handleSelectItem={handleSelectSettingsReservationTypes}
              labelTitle="Reservation Types"
              listItems={reservationTypes}
            />
          </CustomCard>
        )}
        {(isCoach || isSportsClub) && (
          <CustomCard title="Reservation Reminder" width="50%">
            <TextField
              type="number"
              label="Reservation Reminder (Hr)"
              inputProps={{
                min: 0,
              }}
              fullWidth
              variant="outlined"
              size="small"
              value={settings.reservationReminder}
              onChange={(e) =>
                setSettings((prevSettings: any) => ({ ...prevSettings, reservationReminder: e.target.value }))
              }
            />
          </CustomCard>
        )}
        <Box>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={disabled}>
            Submit
          </Button>
        </Box>
      </Box>
    </Loader>
  );
};

export default AccountSettings;
